// Lightweight client-side highlighter for search matches.
// Mirrors the token approach of `websearch_to_tsquery('simple', q)`:
// strip operators, split on whitespace, ignore stop tokens like "or"/"and"/"-foo".

import { sanitizeExcerptHtml } from "@/components/richTextEditorUtils";

const STOP_TOKENS = new Set(["or", "and"]);

export function extractSearchTokens(query: string): string[] {
  if (!query) return [];
  // Drop double-quoted phrase wrappers but keep the inner words as tokens too.
  const cleaned = query.replace(/["']/g, " ");
  return Array.from(
    new Set(
      cleaned
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length >= 2)
        .filter((t) => !t.startsWith("-"))
        .filter((t) => !STOP_TOKENS.has(t.toLowerCase()))
    )
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Wrap matches inside a plain text string with <mark> tags.
 * Returns HTML — caller is responsible for safe rendering.
 */
export function highlightText(text: string, tokens: string[]): string {
  if (!text || tokens.length === 0) return escapeHtml(text);
  const pattern = new RegExp(`(${tokens.map(escapeRegex).join("|")})`, "gi");
  return escapeHtml(text).replace(
    pattern,
    '<mark class="bg-yellow-200 dark:bg-yellow-500/40 text-foreground rounded px-0.5">$1</mark>'
  );
}

/**
 * Wrap matches inside an HTML string with <mark> tags, only touching text nodes
 * (so existing tags/attributes are preserved).
 */
export function highlightHtml(html: string, tokens: string[]): string {
  if (!html) return html;
  // Always sanitize before returning — excerpt body_html is rendered via
  // dangerouslySetInnerHTML, and a workspace member could bypass the
  // frontend editor and write raw HTML directly via the Supabase API.
  const safe = sanitizeExcerptHtml(html);
  if (tokens.length === 0) return safe;
  if (typeof window === "undefined") return safe;
  const pattern = new RegExp(`(${tokens.map(escapeRegex).join("|")})`, "gi");
  const container = document.createElement("div");
  container.innerHTML = safe;
  walk(container, pattern);
  return container.innerHTML;
}

function walk(node: Node, pattern: RegExp) {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent ?? "";
      if (!pattern.test(text)) {
        pattern.lastIndex = 0;
        continue;
      }
      pattern.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let lastIndex = 0;
      text.replace(pattern, (match, _g1, offset: number) => {
        if (offset > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
        }
        const mark = document.createElement("mark");
        mark.className =
          "bg-yellow-200 dark:bg-yellow-500/40 text-foreground rounded px-0.5";
        mark.textContent = match;
        frag.appendChild(mark);
        lastIndex = offset + match.length;
        return match;
      });
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      child.parentNode?.replaceChild(frag, child);
    } else if (
      child.nodeType === Node.ELEMENT_NODE &&
      (child as Element).tagName !== "MARK" &&
      (child as Element).tagName !== "SCRIPT" &&
      (child as Element).tagName !== "STYLE"
    ) {
      walk(child, pattern);
    }
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
